import { effectScope, ref, watch, type Ref } from 'vue'

/**
 * Per-feature payload shape. Bind `Config` to the feature's actual config type
 * (or `null` for boolean-only features).
 */
export type FlagState<Config> = {
  enabled: boolean
  config: Config | null
}

/**
 * Strict shape of the flags returned for the Dashboard app.
 * Add a key here when you create a new feature in the admin under app `dashboard`.
 */
export type DashboardFlags = {
  'offer-engine'?: FlagState<null>
  'marketing-flow-webhook'?: FlagState<null>
  'referral-addon'?: FlagState<null>
  'transactional-automatisation'?: FlagState<null>
  'point-expiration'?: FlagState<null>
  'loyalty-card-merge'?: FlagState<null>
  'api-keys'?: FlagState<null>
  'contest-game-bot-blocking'?: FlagState<null>
}

const FEATURE_FLAGS_URL = import.meta.env.VITE_FEATURE_FLAGS_URL || 'https://flag.heypongo.com'

// Hostname isn't router state, so useRoute() can't replace it here.
// eslint-disable-next-line no-restricted-syntax
const FEATURE_FLAGS_ENV = window.location.hostname.startsWith('sandbox') ? 'sandbox' : 'prod'

const APP_KEY = 'dashboard'

// Shared across every call site so a single fetch populates the whole app.
const flags = ref<DashboardFlags>({}) as Ref<DashboardFlags>
const pending = ref(true)
const error = ref<Error | null>(null)
const lastMerchantId = ref<number | null>(null)
let inflight: Promise<void> | null = null

async function fetchFor(merchantId: number) {
  pending.value = true
  error.value = null
  // Avoid leaking previous merchant's flags during OPS autologin switch.
  flags.value = {}
  try {
    const url = `${FEATURE_FLAGS_URL}/v1/flags?merchant_id=${merchantId}&app_key=${APP_KEY}&env=${FEATURE_FLAGS_ENV}`
    const res = await fetch(url)
    if (!res.ok) throw new Error(`feature-flags: ${res.status}`)
    flags.value = (await res.json()) as DashboardFlags
  } catch (err) {
    error.value = err as Error
    flags.value = {}
    console.error('[useFeatureFlag] fetch failed', err)
  } finally {
    pending.value = false
  }
}

function ensureLoadedFor(merchantId: number) {
  if (lastMerchantId.value === merchantId && inflight) return inflight
  lastMerchantId.value = merchantId
  inflight = fetchFor(merchantId)
  return inflight
}

// Single, app-wide watcher in a detached scope so we don't create a fresh
// watch each time `useFeatureFlag()` is called from a component.
let watching = false
const watcherScope = effectScope(true)
function startWatcher() {
  if (watching) return
  watching = true
  watcherScope.run(() => {
    const { user } = useLocalStorage()
    watch(
      () => user.value?.merchant?.id,
      (id) => {
        if (typeof id === 'number' && id > 0) ensureLoadedFor(id)
      },
      { immediate: true }
    )
  })
}

export function useFeatureFlag() {
  startWatcher()

  const reload = () => {
    inflight = null
    const { user } = useLocalStorage()
    if (typeof user.value?.merchant?.id === 'number') {
      return ensureLoadedFor(user.value.merchant.id)
    }
  }

  return { flags, pending, error, reload }
}
